/**
 * Yields to the main thread once, then runs `cb` on the next macrotask.
 *
 * This splits the long hydration task off from bundle eval / app setup so the
 * browser can paint (FCP) and flush any queued user input before the
 * synchronous hydration pass begins. It is a deliberate Total Blocking Time
 * mitigation, originally introduced in US #162344 ("Split long task to reduce
 * TBT on PD") and kept under the React 18 migration because RUM still shows
 * real load-window main-thread contention (INP inputDelay p75 spikes while the
 * document is in `loading` / `dom-content-loaded`).
 *
 * It replaces the deprecated `setImmediate` + shipped inline polyfill with a
 * native scheduler: `scheduler.postTask` where available, falling back to a
 * `MessageChannel` macrotask — the same mechanism the old polyfill used, but
 * native and no longer injected as a render-blocking inline script.
 */
type PostTaskScheduler = {
  postTask: (cb: () => void, options?: { priority?: string }) => unknown;
};

const getScheduler = (): PostTaskScheduler | undefined => {
  const candidate = (globalThis as { scheduler?: PostTaskScheduler }).scheduler;
  return candidate && typeof candidate.postTask === 'function'
    ? candidate
    : undefined;
};

export const scheduleHydration = (cb: () => void): void => {
  const scheduler = getScheduler();
  if (scheduler) {
    // `user-blocking` keeps hydration starting as soon as possible after the
    // single yield, matching the old setImmediate (high-priority macrotask).
    scheduler.postTask(cb, { priority: 'user-blocking' });
    return;
  }

  if (typeof MessageChannel !== 'undefined') {
    const channel = new MessageChannel();
    channel.port1.onmessage = () => cb();
    channel.port2.postMessage(undefined);
    return;
  }

  // Last-resort fallback (e.g. environments without MessageChannel).
  setTimeout(cb, 0);
};
